import React, { useState, useEffect } from "react";
import axios from "../api/axiosInstance";
import {
  FaEye,
  FaEdit,
  FaFilter,
  FaFileExport,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaInbox,
} from "react-icons/fa";
import Modal from "react-modal";

Modal.setAppElement("#root");

// ---------------------------------------------------------------------
// Small, stateless presentational helpers — kept outside the component
// so they aren't recreated on every render.
// ---------------------------------------------------------------------

const PROFILE_STATUS_STYLES = {
  approve: { label: "Approved", classes: "bg-[#E8F3EA] text-[#2F6B3A]" },
  pending: { label: "Pending", classes: "bg-[#F7E9C9] text-[#8B6A0A]" },
  reject: { label: "Rejected", classes: "bg-[#FBEFF2] text-[#8B1E3F]" },
};

const StatusPill = ({ status }) => {
  const item = PROFILE_STATUS_STYLES[status] || {
    label: status || "—",
    classes: "bg-[#F4F1ED] text-[#8C7B6B]",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${item.classes}`}
    >
      {item.label}
    </span>
  );
};

const ActiveStatusPill = ({ active }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
      active ? "bg-[#E8F3EA] text-[#2F6B3A]" : "bg-[#F4F1ED] text-[#8C7B6B]"
    }`}
  >
    {active ? "Active" : "Inactive"}
  </span>
);

const Avatar = ({ src, name, size = 36 }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={name || "User"}
        className="rounded-full object-cover border border-[#EFE3D0] shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  const initials =
    (name || "?")
      .trim()
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join("") || "?";
  return (
    <span
      className="inline-flex items-center justify-center rounded-full bg-gradient-to-br from-[#9C2C4F] to-[#5C1220] text-white font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials}
    </span>
  );
};

const Field = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-[#A6998C] mb-0.5">
      {label}
    </p>
    <p className="text-sm text-[#4A2E1E] break-words">
      {value === null || value === undefined || value === "" ? "—" : value}
    </p>
  </div>
);

const Paragraph = ({ label, value }) => (
  <div className="sm:col-span-2 lg:col-span-3 min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-[#A6998C] mb-1">
      {label}
    </p>
    <p className="text-sm text-[#4A2E1E] leading-relaxed whitespace-pre-wrap">
      {value || "—"}
    </p>
  </div>
);

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-[#EFE3D0] bg-white p-4 sm:p-5">
    <h4 className="text-xs font-semibold text-[#8B1E3F] uppercase tracking-wider mb-3 pb-2 border-b border-[#F3EADC]">
      {title}
    </h4>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {children}
    </div>
  </div>
);

const TableSkeleton = () => (
  <div className="p-4 space-y-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="flex items-center gap-4 animate-pulse">
        <div className="w-9 h-9 rounded-full bg-[#F3EADC]" />
        <div className="h-3.5 flex-1 rounded bg-[#F3EADC]" />
        <div className="h-3.5 w-20 rounded bg-[#F3EADC] hidden sm:block" />
        <div className="h-3.5 w-20 rounded bg-[#F3EADC] hidden md:block" />
        <div className="h-3.5 w-24 rounded bg-[#F3EADC] hidden lg:block" />
      </div>
    ))}
  </div>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center gap-2 py-14 text-[#A6998C]">
    <FaInbox size={24} />
    <p className="text-sm">No users found.</p>
  </div>
);

// Windowed page list: first, last, current ±1, with gaps collapsed to "…".
// Keeps pagination usable (and on-screen) even with hundreds of pages.
const getPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([
    1,
    2,
    total - 1,
    total,
    current - 1,
    current,
    current + 1,
  ]);
  return Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
};

const UserTable = () => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    return parseInt(localStorage.getItem("pageSize")) || 10;
  });
  const [count, setCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editProfileStatus, setEditProfileStatus] = useState("");
  const [rejectMessage, setRejectMessage] = useState("");

  const [dates, setDates] = useState({
    startDate: "",
    endDate: "",
  });

  // Additional filter states
  const [filters, setFilters] = useState({
    is_active: "",
    gender: "",
    profile_status: "",
    date_joined_start: "",
    date_joined_end: "",
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        search: filterText,
        page,
        page_size: pageSize,
        ...filters,
      };

      const response = await axios.get(`/api/user-list/`, { params });
      setUserData(response.data.results);
      setCount(response.data.count);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  // Refetch on filter/pagination changes
  useEffect(() => {
    fetchUsers();
  }, [filterText, page, pageSize, filters]);

  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const pageNumbers = getPageNumbers(page, totalPages);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const resetFilters = () => {
    setFilters({
      is_active: "",
      gender: "",
      profile_status: "",
      date_joined_start: "",
      date_joined_end: "",
    });
    setPage(1);
  };

  const handleViewClick = async (id) => {
    try {
      const response = await axios.get(`/api/user-detail/${id}/`);
      setSelectedUser(response.data);
      setModalIsOpen(true);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedUser(null);
  };

  // Prevent background scroll on modal open
  useEffect(() => {
    if (modalIsOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [modalIsOpen]);

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditStatus(user.user.is_active ? "Yes" : "No");
    setEditProfileStatus(user.user.profile_status || "pending");
    setRejectMessage("");
    setEditModalOpen(true);
  };

  const handleStatusUpdate = async () => {
    try {
      await axios.put(`/api/user-detail/${editingUser.user.id}/`, {
        is_active: editStatus === "Yes",
      });

      await axios.patch(`/api/user-update/${editingUser.user.id}/`, {
        profile_status: editProfileStatus,
        status_message: editProfileStatus === "reject" ? rejectMessage : "",
      });

      setEditModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error.response || error);
    }
  };

  const handleDownload = async (type) => {
    try {
      let url = "/api/export-profiles/";
      if (type === "filtered" && dates.startDate && dates.endDate) {
        url += `?start_date=${dates.startDate}&end_date=${dates.endDate}`;
      }

      const response = await axios.get(url, { responseType: "blob" });

      const file = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(file);
      link.setAttribute("download", "matrimony_profiles.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowModal(false);
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-5 flex items-start sm:items-center justify-between gap-3 ">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-[#4A2E1E]">
            User Management
          </h2>
          <div className="mt-2 h-1 w-14 rounded-full bg-gradient-to-r from-[#8B1E3F] to-[#C9A227]" />
        </div>
        <span className="text-xs sm:text-sm text-[#8C7B6B]">
          {count.toLocaleString()} total {count === 1 ? "user" : "users"}
        </span>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E9C3CD] text-[#8B1E3F] text-sm font-medium bg-white hover:bg-[#FBEFF2] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]"
        >
          <FaFilter size={13} /> {showFilters ? "Hide Filters" : "Filters"}
        </button>

        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#8B1E3F] to-[#6E1228] hover:opacity-90 transition-opacity shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]"
        >
          <FaFileExport size={13} /> Export to Excel
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="rounded-xl border border-[#EFE3D0] bg-white p-4 sm:p-5 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="is_active"
                className="text-xs font-medium text-[#6B5E52]"
              >
                Active Status
              </label>
              <select
                id="is_active"
                name="is_active"
                value={filters.is_active}
                onChange={handleFilterChange}
                className="border border-[#EFE3D0] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              >
                <option value="">Any</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="gender"
                className="text-xs font-medium text-[#6B5E52]"
              >
                Gender
              </label>
              <select
                id="gender"
                name="gender"
                value={filters.gender}
                onChange={handleFilterChange}
                className="border border-[#EFE3D0] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              >
                <option value="">Any</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="profile_status"
                className="text-xs font-medium text-[#6B5E52]"
              >
                Profile Status
              </label>
              <select
                id="profile_status"
                name="profile_status"
                value={filters.profile_status}
                onChange={handleFilterChange}
                className="border border-[#EFE3D0] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              >
                <option value="">Any</option>
                <option value="approve">Approve</option>
                <option value="pending">Pending</option>
                <option value="reject">Reject</option>
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="date_joined_start"
                className="text-xs font-medium text-[#6B5E52]"
              >
                Start Date
              </label>
              <input
                type="date"
                id="date_joined_start"
                name="date_joined_start"
                value={filters.date_joined_start}
                onChange={handleFilterChange}
                className="border border-[#EFE3D0] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="date_joined_end"
                className="text-xs font-medium text-[#6B5E52]"
              >
                End Date
              </label>
              <input
                type="date"
                id="date_joined_end"
                name="date_joined_end"
                value={filters.date_joined_end}
                onChange={handleFilterChange}
                className="border border-[#EFE3D0] rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              />
            </div>

            <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-3 xl:col-span-5">
              <button
                onClick={() => {
                  setPage(1);
                  fetchUsers();
                }}
                className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#C9A227] to-[#8B1E3F] hover:opacity-90 transition-opacity"
              >
                Apply Filters
              </button>
              <button
                onClick={resetFilters}
                className="px-5 py-2 rounded-lg text-sm font-medium border border-[#EFE3D0] text-[#6B5E52] hover:bg-[#F4F1ED] transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search + page size */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="relative w-full sm:max-w-sm">
          <FaSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A6998C]"
            size={13}
          />
          <input
            type="text"
            placeholder="Search by name, email or phone..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#EFE3D0] bg-white text-sm text-[#4A2E1E] placeholder:text-[#C9BBA8] focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
            value={filterText}
            onChange={(e) => {
              setPage(1);
              setFilterText(e.target.value);
            }}
          />
        </div>

        <div className="flex items-center gap-2 text-sm text-[#6B5E52]">
          <label htmlFor="pageSize" className="font-medium">
            Items per page:
          </label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(Number(e.target.value));
              localStorage.setItem("pageSize", e.target.value);
            }}
            className="border border-[#EFE3D0] rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Table card */}
      <div className="rounded-xl border border-[#EFE3D0] bg-white overflow-hidden shadow-sm">
        {loading ? (
          <TableSkeleton />
        ) : userData.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FBF3E7]">
                  <tr>
                    {[
                      "User",
                      "Phone",
                      "Gender",
                      "Status",
                      "Profile Status",
                      "Date Joined",
                      "Actions",
                    ].map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-left font-semibold text-[#6E1228] border-b border-[#EFE3D0] whitespace-nowrap"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {userData.map((user, i) => (
                    <tr
                      key={user.user.id}
                      className={`${
                        i % 2 === 0 ? "bg-white" : "bg-[#FFFBF2]"
                      } hover:bg-[#FBEFF2]/60 transition-colors`}
                    >
                      <td className="px-4 py-3 border-b border-[#F3EADC]">
                        <div className="flex items-center gap-3">
                          <Avatar
                            src={user.photo}
                            name={`${user.user.first_name} ${user.user.last_name}`}
                          />
                          <div className="min-w-0">
                            <p className="font-medium text-[#4A2E1E] truncate max-w-[200px]">
                              {user.user.first_name} {user.user.last_name}
                            </p>
                            <p className="text-xs text-[#A6998C] truncate max-w-[200px]">
                              {user.user.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b border-[#F3EADC] text-[#5A4A3D] whitespace-nowrap">
                        {user.phone || "—"}
                      </td>
                      <td className="px-4 py-3 border-b border-[#F3EADC] text-[#5A4A3D] whitespace-nowrap">
                        {user.gender || "—"}
                      </td>
                      <td className="px-4 py-3 border-b border-[#F3EADC]">
                        <ActiveStatusPill active={user.user.is_active} />
                      </td>
                      <td className="px-4 py-3 border-b border-[#F3EADC]">
                        <StatusPill status={user.profile_status} />
                      </td>
                      <td className="px-4 py-3 border-b border-[#F3EADC] text-[#5A4A3D] whitespace-nowrap">
                        {new Date(user.user.date_joined).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 border-b border-[#F3EADC]">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleViewClick(user.user.id)}
                            title="View details"
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full text-[#8B1E3F] hover:bg-[#FBEFF2] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]"
                          >
                            <FaEye size={14} />
                          </button>
                          <button
                            onClick={() => handleEditClick(user)}
                            title="Edit status"
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full text-[#B8860B] hover:bg-[#FBF3E7] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]"
                          >
                            <FaEdit size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden p-3 space-y-3">
              {userData.map((user) => (
                <div
                  key={user.user.id}
                  className="rounded-xl border border-[#EFE3D0] bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar
                        src={user.photo}
                        name={`${user.user.first_name} ${user.user.last_name}`}
                        size={40}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#4A2E1E] truncate">
                          {user.user.first_name} {user.user.last_name}
                        </p>
                        <p className="text-xs text-[#A6998C] truncate">
                          {user.user.email}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <ActiveStatusPill active={user.user.is_active} />
                      <StatusPill status={user.profile_status} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-[#6B5E52] mb-3">
                    <p>
                      <span className="text-[#A6998C]">Phone: </span>
                      {user.phone || "—"}
                    </p>
                    <p>
                      <span className="text-[#A6998C]">Gender: </span>
                      {user.gender || "—"}
                    </p>
                    <p>
                      <span className="text-[#A6998C]">Joined: </span>
                      {new Date(user.user.date_joined).toLocaleDateString()}
                    </p>
                    <p>
                      <span className="text-[#A6998C]">ID: </span>#
                      {user.user.id}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-[#F3EADC]">
                    <button
                      onClick={() => handleViewClick(user.user.id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E9C3CD] text-[#8B1E3F] text-xs font-medium hover:bg-[#FBEFF2] transition-colors"
                    >
                      <FaEye size={12} /> View
                    </button>
                    <button
                      onClick={() => handleEditClick(user)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#EFE3D0] text-[#B8860B] text-xs font-medium hover:bg-[#FBF3E7] transition-colors"
                    >
                      <FaEdit size={12} /> Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && userData.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-[#F3EADC] bg-[#FFFCF7]">
            <p className="text-xs sm:text-sm text-[#8C7B6B]">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-1 overflow-x-auto">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-[#6B5E52] hover:bg-[#F4F1ED] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <FaChevronLeft size={11} />
              </button>

              {pageNumbers.map((p, i) => {
                const prev = pageNumbers[i - 1];
                const showEllipsis = prev !== undefined && p - prev > 1;
                return (
                  <React.Fragment key={p}>
                    {showEllipsis && (
                      <span className="px-1 text-[#C9BBA8] text-xs">…</span>
                    )}
                    <button
                      onClick={() => handlePageChange(p)}
                      className={`min-w-[32px] px-2.5 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                        page === p
                          ? "bg-gradient-to-r from-[#8B1E3F] to-[#6E1228] text-white"
                          : "text-[#6B5E52] hover:bg-[#F4F1ED]"
                      }`}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-[#6B5E52] hover:bg-[#F4F1ED] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <FaChevronRight size={11} />
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="User Details"
        className="bg-[#FBF7F0] w-full max-w-6xl  ml-64 my-6 sm:my-10 rounded-2xl shadow-2xl outline-none max-h-[90vh] overflow-y-auto"
        overlayClassName="fixed inset-0 bg-[#3A2417]/40 backdrop-blur-sm z-50 flex justify-center items-start px-3 sm:px-4 overflow-y-auto"
      >
        <div className="sticky top-0 z-10 bg-[#FBF7F0] border-b  border-[#EFE3D0] px-4 sm:px-6 py-4 flex items-center justify-between gap-3 rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0">
            {selectedUser && (
              <Avatar
                src={selectedUser.user?.photo}
                name={`${selectedUser.user.first_name} ${selectedUser.user.last_name}`}
                size={48}
              />
            )}
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-[#4A2E1E] truncate">
                {selectedUser
                  ? `${selectedUser.user.first_name} ${selectedUser.user.last_name}`
                  : "User Details"}
              </h2>
              {selectedUser && (
                <p className="text-xs text-[#A6998C] truncate">
                  {selectedUser.user.email}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={closeModal}
            aria-label="Close"
            className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-full border border-[#E9C3CD] text-[#8B1E3F] hover:bg-[#FBEFF2] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A227]"
          >
            <FaTimes size={14} />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {selectedUser ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <ActiveStatusPill active={selectedUser.user.is_active} />
                <StatusPill status={selectedUser.profile?.profile_status} />
              </div>

              <Section title="Account Info">
                <Field label="User ID" value={selectedUser.user.id} />
                <Field label="Username" value={selectedUser.user.username} />
                <Field label="Email" value={selectedUser.user.email} />
                <Field
                  label="First Name"
                  value={selectedUser.user.first_name}
                />
                <Field label="Last Name" value={selectedUser.user.last_name} />
                <Field
                  label="Is Staff"
                  value={selectedUser.user.is_staff ? "Yes" : "No"}
                />
                <Field
                  label="Is Superuser"
                  value={selectedUser.user.is_superuser ? "Yes" : "No"}
                />
                <Field
                  label="Is Active"
                  value={selectedUser.user.is_active ? "Active" : "Inactive"}
                />
                <Field
                  label="Last Login"
                  value={selectedUser.user.last_login}
                />
                <Field
                  label="Date Joined"
                  value={new Date(
                    selectedUser.user.date_joined,
                  ).toLocaleDateString()}
                />
              </Section>

              <Section title="Personal Details">
                <Field
                  label="Profile ID"
                  value={selectedUser.profile?.profile_id}
                />
                <Field label="Gender" value={selectedUser.profile?.gender} />
                <Field
                  label="Date of Birth"
                  value={selectedUser.profile?.dob}
                />
                <Field
                  label="Time of Birth"
                  value={selectedUser.profile?.time_of_birth}
                />
                <Field
                  label="Place of Birth"
                  value={selectedUser.profile?.place_of_birth}
                />
                <Field label="Weight" value={selectedUser.profile?.weight} />
                <Field
                  label="Height"
                  value={
                    selectedUser.profile?.height_feet != null ||
                    selectedUser.profile?.height_inches != null
                      ? `${selectedUser.profile?.height_feet ?? "—"}ft ${
                          selectedUser.profile?.height_inches ?? "—"
                        }in`
                      : null
                  }
                />
                <Field label="Mobile No" value={selectedUser.profile?.phone} />
                <Field label="Email" value={selectedUser.profile?.email} />
                <Field
                  label="Marital Status"
                  value={selectedUser.profile?.marital_status}
                />
                <Field label="Manglik" value={selectedUser.profile?.manglik} />
                <Field
                  label="Any Disability"
                  value={selectedUser.profile?.any_disability}
                />
                <Field
                  label="Physical Status"
                  value={selectedUser.profile?.physical_status}
                />
              </Section>

              <Section title="Religion & Lineage">
                <Field
                  label="Religion"
                  value={selectedUser.profile?.religion}
                />
                <Field label="Caste" value={selectedUser.profile?.caste} />
                <Field
                  label="Subcaste"
                  value={selectedUser.profile?.subcaste}
                />
                <Field label="Gothra" value={selectedUser.profile?.gothra} />
                <Field label="Kul" value={selectedUser.profile?.kul} />
                <Field label="Kuldevi" value={selectedUser.profile?.kuldevi} />
                <Field
                  label="Kuldevta"
                  value={selectedUser.profile?.kuldevta}
                />
                <Field label="Vansh" value={selectedUser.profile?.vansh} />
                <Field
                  label="Mother Tongue"
                  value={selectedUser.profile?.mother_tongue}
                />
                <Field
                  label="Ancestral Origin"
                  value={selectedUser.profile?.ancestral_origin}
                />
              </Section>

              <Section title="Education & Career">
                <Field
                  label="Highest Education"
                  value={selectedUser.profile?.highest_education}
                />
                <Field
                  label="College Name"
                  value={selectedUser.profile?.college_name}
                />
                <Field
                  label="Employed In"
                  value={selectedUser.profile?.employed_in}
                />
                <Field
                  label="Occupation"
                  value={selectedUser.profile?.occupation}
                />
                <Field
                  label="Job Company Name"
                  value={selectedUser.profile?.job_company_name}
                />
                <Field
                  label="Annual Income"
                  value={selectedUser.profile?.annual_income}
                />
                <Field
                  label="Work Country"
                  value={selectedUser.profile?.work_location_country}
                />
                <Field
                  label="Work State"
                  value={selectedUser.profile?.work_location_state}
                />
                <Field
                  label="Work City"
                  value={selectedUser.profile?.work_location_city}
                />
              </Section>

              <Section title="Family Details">
                <Field
                  label="Family Status"
                  value={selectedUser.profile?.family_status}
                />
                <Field
                  label="Family Type"
                  value={selectedUser.profile?.family_type}
                />
                <Field
                  label="Family Values"
                  value={selectedUser.profile?.family_values}
                />
                <Field
                  label="Family Location"
                  value={selectedUser.profile?.family_location}
                />
                <Field
                  label="Father's Occupation"
                  value={selectedUser.profile?.fathers_occupation}
                />
                <Field
                  label="Mother's Occupation"
                  value={selectedUser.profile?.mothers_occupation}
                />
                <Field
                  label="No. of Brothers"
                  value={selectedUser.profile?.no_of_brothers}
                />
                <Field
                  label="No. of Sisters"
                  value={selectedUser.profile?.no_of_sisters}
                />
                <Field
                  label="Brother Qualification"
                  value={selectedUser.profile?.brother_qualification}
                />
                <Field
                  label="Sister Qualification"
                  value={selectedUser.profile?.sister_qualification}
                />
                <Field
                  label="Brother Occupation"
                  value={selectedUser.profile?.brother_occupation}
                />
                <Field
                  label="Sister Occupation"
                  value={selectedUser.profile?.sister_occupation}
                />
              </Section>

              <Section title="Maternal Family">
                <Field
                  label="Mama ka Naam"
                  value={selectedUser.profile?.mama_name}
                />
                <Field
                  label="Nana ji ka Naam"
                  value={selectedUser.profile?.nana_name}
                />
                <Field
                  label="Mama Gotra"
                  value={selectedUser.profile?.mama_gotra}
                />
                <Field
                  label="Mama Kuldevi"
                  value={selectedUser.profile?.mama_kuldevi}
                />
                <Field
                  label="Mama Place"
                  value={selectedUser.profile?.mama_place}
                />
              </Section>

              <Section title="Lifestyle">
                <Field
                  label="Drinking Habits"
                  value={selectedUser.profile?.drinking_habits}
                />
                <Field
                  label="Smoking Habits"
                  value={selectedUser.profile?.smoking_habits}
                />
                <Field
                  label="Eating Habits"
                  value={selectedUser.profile?.eating_habits}
                />
                <Field
                  label="Willing to Marry Other Communities"
                  value={
                    selectedUser.profile?.willing_other_communities
                      ? "Yes"
                      : "No"
                  }
                />
              </Section>

              <Section title="About">
                <Paragraph
                  label="About Me"
                  value={selectedUser.profile?.about_me}
                />
                <Paragraph
                  label="About Family"
                  value={selectedUser.profile?.about_family}
                />
                <Paragraph
                  label="Hobbies & Interests"
                  value={selectedUser.profile?.hobbies_and_interests}
                />
                <Paragraph label="About" value={selectedUser.profile?.about} />
              </Section>

              <Section title="Profile Status & Completion">
                <Field
                  label="Profile Status"
                  value={selectedUser.profile?.profile_status}
                />
                <Field
                  label="Status Message"
                  value={selectedUser.profile?.status_message}
                />
                <Field
                  label="Profile Completed"
                  value={selectedUser.profile?.profile_completed ? "Yes" : "No"}
                />
                <Field
                  label="Profile Completion %"
                  value={
                    selectedUser.profile?.profile_completion_percentage != null
                      ? `${selectedUser.profile.profile_completion_percentage}%`
                      : null
                  }
                />
                <Field
                  label="Badge Awarded"
                  value={
                    selectedUser.profile?.profile_completion_badge_awarded
                      ? "Yes"
                      : "No"
                  }
                />
                <Field
                  label="Completion Awarded At"
                  value={selectedUser.profile?.completion_awarded_at}
                />
                <Field
                  label="Created At"
                  value={selectedUser.profile?.created_at}
                />
              </Section>
            </div>
          ) : (
            <p className="text-center text-[#8C7B6B] py-10">Loading...</p>
          )}
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onRequestClose={() => {}}
        contentLabel="Edit User Status"
        className="relative bg-white p-6 rounded-2xl shadow-2xl w-[92%] max-w-lg  border border-[#EFE3D0] outline-none"
        overlayClassName="fixed inset-0 bg-[#3A2417]/30 backdrop-blur-sm flex justify-center items-center z-50 px-3"
      >
        <h2 className="text-lg sm:text-xl font-semibold text-[#4A2E1E] mb-6 text-center border-b border-[#F3EADC] pb-3">
          Edit User Status
        </h2>

        {editingUser && (
          <div className="space-y-5">
            <div>
              <label className="block mb-1.5 text-sm font-medium text-[#6B5E52]">
                Is Active
              </label>
              <select
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                className="w-full border border-[#EFE3D0] px-4 py-2 rounded-lg focus:ring-2 focus:ring-[#C9A227] outline-none text-sm"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <div>
              <label className="block mb-1.5 text-sm font-medium text-[#6B5E52]">
                Profile Status
              </label>
              <select
                value={editProfileStatus}
                onChange={(e) => setEditProfileStatus(e.target.value)}
                className="w-full border border-[#EFE3D0] px-4 py-2 rounded-lg focus:ring-2 focus:ring-[#C9A227] outline-none text-sm"
              >
                <option value="approve">Approve</option>
                <option value="pending">Pending</option>
                <option value="reject">Reject</option>
              </select>
            </div>

            {editProfileStatus === "reject" && (
              <div>
                <label className="block mb-1.5 text-sm font-medium text-[#6B5E52]">
                  Reject Reason
                </label>
                <textarea
                  value={rejectMessage}
                  onChange={(e) => setRejectMessage(e.target.value)}
                  placeholder="Enter reason for rejection"
                  rows={3}
                  className="w-full border border-[#EFE3D0] px-4 py-2 rounded-lg focus:ring-2 focus:ring-[#8B1E3F] outline-none text-sm"
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-[#F3EADC]">
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="px-5 py-2 rounded-lg text-sm font-medium border border-[#EFE3D0] text-[#6B5E52] hover:bg-[#F4F1ED] transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={handleStatusUpdate}
                className="px-5 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#8B1E3F] to-[#6E1228] hover:opacity-90 transition-opacity shadow-sm"
              >
                Save Changes
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Export Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#3A2417]/30 backdrop-blur-sm px-3">
          <div className="w-full max-w-md bg-white p-5 sm:p-6 rounded-2xl shadow-2xl border border-[#EFE3D0]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#4A2E1E]">
                Export Profiles
              </h2>
              <button
                onClick={() => setShowModal(false)}
                aria-label="Close"
                className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-[#EFE3D0] text-[#6B5E52] hover:bg-[#F4F1ED] transition-colors"
              >
                <FaTimes size={13} />
              </button>
            </div>

            <div className="space-y-3 mb-5">
              <label className="block">
                <span className="block mb-1 text-sm font-medium text-[#6B5E52]">
                  Start Date
                </span>
                <input
                  type="date"
                  className="w-full border border-[#EFE3D0] px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  value={dates.startDate}
                  onChange={(e) =>
                    setDates({ ...dates, startDate: e.target.value })
                  }
                />
              </label>

              <label className="block">
                <span className="block mb-1 text-sm font-medium text-[#6B5E52]">
                  End Date
                </span>
                <input
                  type="date"
                  className="w-full border border-[#EFE3D0] px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                  value={dates.endDate}
                  onChange={(e) =>
                    setDates({ ...dates, endDate: e.target.value })
                  }
                />
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => handleDownload("filtered")}
                disabled={!dates.startDate || !dates.endDate}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border border-[#E9C3CD] text-[#8B1E3F] hover:bg-[#FBEFF2] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Download Filtered
              </button>
              <button
                onClick={() => handleDownload("all")}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#8B1E3F] to-[#6E1228] hover:opacity-90 transition-opacity shadow-sm"
              >
                Download All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserTable;
